import { useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent } from "react";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import { isOfflineApiError, providerApi, type ProviderMediaPhoto } from "../../lib/api";
import { useProviderOffline } from "../../lib/provider-offline";
import { useAppPreferences } from "../../lib/preferences";
import {
  getProviderImageErrorMessage,
  uploadProviderImage,
} from "./provider-image-upload";

type PhotoOwner =
  | {
      limit: number;
      ownerId: string;
      ownerType: "venue";
      photos: ProviderMediaPhoto[];
      title: string;
      onUpdated: () => Promise<void>;
    }
  | {
      limit: number;
      ownerId: string;
      ownerType: "unit";
      photos: ProviderMediaPhoto[];
      title: string;
      venueId: string;
      onUpdated: () => Promise<void>;
    };

export function ProviderPhotoManager(props: PhotoOwner) {
  const { t } = useAppPreferences();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const { confirm, dialog } = useHardConfirm();
  const { isOnline, queueMutation } = useProviderOffline();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const isAtLimit = props.photos.length >= props.limit;
  const canUpload = !isWorking && !isAtLimit && isOnline;

  const uploadPhoto = async (file: File, isPrimary: boolean) => {
    setError(null);
    setIsWorking(true);

    try {
      const { file: preparedFile, signed } = await uploadProviderImage(props.ownerType, props.ownerId, file);

      const payload = {
        alt_text: `${props.title} photo`,
        content_type: signed.content_type,
        is_primary: isPrimary,
        size_bytes: preparedFile.size,
        storage_key: signed.storage_key,
        url: signed.public_url,
      };

      if (props.ownerType === "venue") {
        await providerApi.createVenuePhoto(props.ownerId, payload);
      } else {
        await providerApi.createUnitPhoto(props.venueId, props.ownerId, payload);
      }

      await props.onUpdated();
      return true;
    } catch (uploadError) {
      setError(getProviderImageErrorMessage(uploadError, t));
      return false;
    } finally {
      setIsWorking(false);
    }
  };

  const uploadFiles = async (files: File[]) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const skippedNonImages = files.length - imageFiles.length;

    if (imageFiles.length === 0) {
      if (skippedNonImages > 0) {
        setError(t("Only image files can be uploaded."));
      }
      return;
    }

    const remainingSlots = Math.max(0, props.limit - props.photos.length);
    const queue = imageFiles.slice(0, remainingSlots);
    const skippedByLimit = imageFiles.length - queue.length;

    setIsUploading(true);
    try {
      let uploadedCount = 0;
      for (const file of queue) {
        const succeeded = await uploadPhoto(file, props.photos.length === 0 && uploadedCount === 0);
        if (!succeeded) {
          return;
        }
        uploadedCount += 1;
      }

      if (skippedNonImages > 0) {
        setError(t("Only image files can be uploaded."));
      } else if (skippedByLimit > 0) {
        setError(t("Photo limit reached — some images were not uploaded."));
      }
    } finally {
      setIsUploading(false);
    }
  };

  const openFilePicker = () => {
    if (!canUpload) {
      return;
    }
    inputRef.current?.click();
  };

  const eventHasDraggedFiles = (event: DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types).includes("Files");

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!eventHasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepthRef.current += 1;
    if (canUpload) {
      setIsDragActive(true);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!eventHasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = canUpload ? "copy" : "none";
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!eventHasDraggedFiles(event)) {
      return;
    }
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!eventHasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    if (!canUpload) {
      return;
    }
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      void uploadFiles(droppedFiles);
    }
  };

  const handleSurfaceClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, textarea, select")) {
      return;
    }
    openFilePicker();
  };

  const handleSurfaceKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const updatePhoto = async (photoId: string, isPrimary: boolean) => {
    setError(null);
    setNotice(null);
    setIsWorking(true);
    try {
      if (props.ownerType === "venue") {
        await providerApi.updateVenuePhoto(props.ownerId, photoId, { is_primary: isPrimary });
      } else {
        await providerApi.updateUnitPhoto(props.venueId, props.ownerId, photoId, { is_primary: isPrimary });
      }
      await props.onUpdated();
    } catch (photoError) {
      if (isOfflineApiError(photoError)) {
        const path =
          props.ownerType === "venue"
            ? `/api/v1/provider/venues/${props.ownerId}/photos/${photoId}`
            : `/api/v1/provider/venues/${props.venueId}/units/${props.ownerId}/photos/${photoId}`;
        queueMutation({
          method: "PATCH",
          path,
          body: { is_primary: isPrimary },
          label: `Set primary photo for ${props.title}`,
        });
        setNotice(t("Photo change saved offline. It will sync when connection returns."));
      } else {
        setError(getProviderImageErrorMessage(photoError, t));
      }
    } finally {
      setIsWorking(false);
    }
  };

  const deletePhoto = async (photoId: string) => {
    const isConfirmed = await confirm({
      confirmationValue: "DELETE",
      confirmButtonLabel: t("Delete photo"),
      consequence: t("The image will disappear from customer-facing galleries and provider workspace cards."),
      description: t("This permanently removes the selected photo from {name}.", { name: props.title }),
      title: t("Delete a photo from {name}?", { name: props.title }),
    });
    if (!isConfirmed) {
      return;
    }

    setError(null);
    setNotice(null);
    setIsWorking(true);
    try {
      if (props.ownerType === "venue") {
        await providerApi.deleteVenuePhoto(props.ownerId, photoId);
      } else {
        await providerApi.deleteUnitPhoto(props.venueId, props.ownerId, photoId);
      }
      await props.onUpdated();
    } catch (photoError) {
      if (isOfflineApiError(photoError)) {
        const path =
          props.ownerType === "venue"
            ? `/api/v1/provider/venues/${props.ownerId}/photos/${photoId}`
            : `/api/v1/provider/venues/${props.venueId}/units/${props.ownerId}/photos/${photoId}`;
        queueMutation({
          method: "DELETE",
          path,
          body: undefined,
          label: `Delete photo from ${props.title}`,
        });
        setNotice(t("Photo deletion saved offline. It will sync when connection returns."));
      } else {
        setError(getProviderImageErrorMessage(photoError, t));
      }
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div
      aria-busy={isUploading}
      aria-disabled={!canUpload}
      aria-label={t("Upload photos for {name}", { name: props.title })}
      className={`ops-subtle-surface relative grid gap-3 p-3 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 ${
        canUpload ? "cursor-pointer" : ""
      }`}
      onClick={handleSurfaceClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onKeyDown={handleSurfaceKeyDown}
      role="button"
      tabIndex={canUpload ? 0 : -1}
    >
      {dialog}

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="ops-label">{t("Photos")}</p>
          <p className="text-xs text-slate-400">
            {t("{used} of {limit} photos. The main photo is shown first in the app. You can also drop images here.", { used: props.photos.length, limit: props.limit })}
          </p>
        </div>
        <button
          className="ops-button"
          data-size="sm"
          data-variant="secondary"
          disabled={isWorking || isAtLimit || !isOnline}
          onClick={() => inputRef.current?.click()}
          type="button"
        >
          {isWorking ? t("Working...") : !isOnline ? t("Upload unavailable offline") : t("Upload photo")}
        </button>
        <input
          accept="image/*"
          className="hidden"
          disabled={isWorking || isAtLimit || !isOnline}
          multiple
          onChange={(event) => {
            const selectedFiles = Array.from(event.target.files ?? []);
            event.target.value = "";
            if (selectedFiles.length > 0) {
              void uploadFiles(selectedFiles);
            }
          }}
          ref={inputRef}
          type="file"
        />
      </div>

      {notice ? (
        <div className="ops-banner" data-tone="success" role="status">
          {notice}
        </div>
      ) : null}

      {error ? (
        <div className="ops-banner" data-tone="danger" role="alert">
          {error}
        </div>
      ) : null}

      {props.photos.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {props.photos.map((photo) => (
            <div className="overflow-hidden rounded-[14px] border border-white/10 bg-slate-950/45" key={photo.id}>
              <img alt={photo.alt_text ?? props.title} className="h-24 w-full object-cover" src={photo.url} />
              <div className="grid gap-2 p-2">
                <div className="flex items-center gap-2">
                  {photo.is_primary ? (
                    <span className="ops-chip flex-1 justify-center" data-tone="success">{t("Main photo")}</span>
                  ) : (
                    <button
                      className="ops-button flex-1"
                      data-size="sm"
                      data-variant="secondary"
                      disabled={isWorking}
                      onClick={() => void updatePhoto(photo.id, true)}
                      type="button"
                    >
                      {t("Make main photo")}
                    </button>
                  )}
                  <button
                    className="ops-button"
                    data-size="sm"
                    data-variant="danger"
                    disabled={isWorking}
                    onClick={() => void deletePhoto(photo.id)}
                    type="button"
                  >
                    {t("Delete")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">{t("No photos yet.")}</p>
      )}

      {isDragActive ? (
        <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-[12px] border-2 border-dashed border-[var(--color-signal)] bg-[var(--color-signal-muted)]">
          <p className="text-sm font-semibold text-[var(--color-accent)]">{t("Drop images to upload")}</p>
        </div>
      ) : null}

      {isUploading ? (
        <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-[12px] bg-slate-950/70" role="status">
          <p className="text-sm font-semibold text-[var(--color-accent)]">{t("Uploading photos...")}</p>
        </div>
      ) : null}
    </div>
  );
}
